/**
 * Fortsetzung: ein Abschneiden nach „Absenden nach Stille“ wird erkannt und angehängt. (12.10.2026, Bahn „fortsetzung-anhaengen“)
 *
 * Martin: *„Warum dauert das Absenden nach einem fertigen Satz ewig?“* Das System kann einen fertigen Satz von einer Denkpause über
 * Text und Jev allein nicht sicher trennen (shared/stille-auto.ts, `TEMPO_STUFEN`). Schneller senden heißt also öfter mitten in der
 * Äußerung abschicken — und das war bisher unsichtbar und teuer: kein Anschließen, die Antwort auf den ersten Teil lief, der Knopf war
 * bis zu ihrem Ende gesperrt, der Rest ging als neue, getrennte Nachricht hinaus. Darum zuerst die Reparatur: beginnt Martin binnen
 * `FORTSETZUNG_FENSTER_MS` nach einem Auto-Senden eine neue Aufnahme, gilt sie als Fortsetzung der vorigen Äußerung.
 *
 * ## Wer was tut (Beschreibung und Entscheidungen: docs/fortsetzung-2026-10-12.md)
 *
 *   App     Beim START der Aufnahme: den Knopf in diesem Fenster freigeben, die Antwort der Vorrunde abbrechen (POST /api/chat/abort),
 *           die Wiedergabe stoppen, die Aufnahme mit `fortsetzung` markieren (frontend/lib/fortsetzung.ts).
 *   Server  Prüft das Feld (`pruefeFortsetzung`) — sonst wird es ignoriert —, stellt dem Modell EINEN festen Hinweis voran
 *           (`mitFortsetzungsHinweis`), unterbricht die Vorrunde selbst, falls sie noch läuft, und markiert den Verlauf.
 *
 * ## Das Feld ist Nutzer-Eingabe, kein Fremdtext — und trotzdem wird es geprüft
 *
 * Es kommt nur von der Sprechtaste von Konto 1 (`/api/voice/command-stream`). Telegram, Anruf, Chat und `/api/voice/erkennen` lesen es
 * nie. Es trägt nur Zahlen und feste Wörter (`leseFortsetzungAngabe`); der Hinweis an das Modell ist fester Text im Code, nie ein Wort
 * aus dem Gesagten. Es hebt keine Sperre auf: Zustimmungs- und Fremdtext-Sperren lesen die NEUE Aufnahme allein
 * (`anfrageZug.nachricht`); ein „Ja“ zählt wie in jeder neuen Nachricht, nie als Summe beider Hälften.
 */

/** Wie lange nach einem Auto-Senden eine neue Aufnahme als Fortsetzung gilt (ms) — gemessen in der App vom Absenden bis zum Aufnahmestart. */
export const FORTSETZUNG_FENSTER_MS = 12_000;
/** Spielraum des Servers über dem Fenster (Prellen des Knopfes, Uhren, Verzögerung bis zum Start). */
export const FORTSETZUNG_TOLERANZ_MS = 3_000;
/**
 * Wie viel älter als `abstandMs` die vorige Nachricht beim Server höchstens sein darf: so lange darf die NEUE Aufnahme dauern, bis sie
 * ankommt. Das hält alte Kennungen draußen, ohne eine lange Fortsetzung abzuweisen.
 */
export const FORTSETZUNG_AUFNAHME_MAX_MS = 5 * 60_000;
/** Höchstens so viele gültige Fortsetzungen je Konto und Minute — mehr ist ein Fehler der Seite, keine Nutzung. */
export const FORTSETZUNG_MAX_JE_MINUTE = 6;

/** Das Satzende der vorigen Nachricht — die Art (shared/stille-auto.ts `SatzEnde`) oder „-“, wenn kein Urteil da war. */
export type FortsetzungSatz = "?" | "." | "!" | "keins" | "-";
/** `lief`: die Wiedergabe der Vorrunde spielte im Moment des Aufnahmestarts (die Seite hat sie gestoppt), `nein`: sie war still. */
type FortsetzungTts = "lief" | "nein";

/** Was die Seite mit der Aufnahme schickt — nur Zahlen und feste Wörter, alles im Moment des Auto-Sendens bzw. des Aufnahmestarts. */
export interface FortsetzungAngabe {
  /** Die `anfrageKennung` der vorigen (per Auto-Senden abgeschickten) Nachricht. */
  vorherId: string;
  /** Vom Auto-Senden bis zum Beginn dieser Aufnahme (ms). */
  abstandMs: number;
  /** Jevs Urteil für den vorigen Text beim Absenden, `null`: keines. */
  jev: number | null;
  satz: FortsetzungSatz;
  /** Die Stille beim Absenden (ms), `null`: unbekannt. */
  stillMs: number | null;
  tts: FortsetzungTts;
}

/** Eine Kennung der Seite: Buchstaben, Ziffern, `_` und `-` (UUID oder `ptt-<zeit>-<zufall>`). */
const KENNUNG_MUSTER = /^[A-Za-z0-9_-]{8,100}$/;
const SAETZE: ReadonlySet<string> = new Set(["?", ".", "!", "keins", "-"]);
/** So lang darf das Feld im Rumpf höchstens sein (es trägt nur Zahlen und feste Wörter). */
const FELD_MAX_ZEICHEN = 400;

const istZahl = (v: unknown, von: number, bis: number): v is number =>
  typeof v === "number" && Number.isFinite(v) && v >= von && v <= bis;

/**
 * Das Feld `fortsetzung` aus dem Rumpf der Anfrage: ein JSON-Text (Formular) oder schon ein Objekt. Liefert die Angabe — oder `null`,
 * sobald IRGENDETWAS nicht stimmt (falscher Typ, fremde Wörter, Zahl außerhalb, Kennung mit Sonderzeichen). Nie ein Wurf.
 */
export function leseFortsetzungAngabe(roh: unknown): FortsetzungAngabe | null {
  let wert: unknown = roh;
  if (typeof roh === "string") {
    if (roh.length === 0 || roh.length > FELD_MAX_ZEICHEN) return null;
    try {
      wert = JSON.parse(roh) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof wert !== "object" || wert === null || Array.isArray(wert)) return null;
  const o = wert as Record<string, unknown>;
  const { vorherId, abstandMs, jev, satz, stillMs, tts } = o;
  if (typeof vorherId !== "string" || !KENNUNG_MUSTER.test(vorherId)) return null;
  if (!istZahl(abstandMs, 0, 600_000)) return null;
  if (jev !== null && !istZahl(jev, 0, 1)) return null;
  if (typeof satz !== "string" || !SAETZE.has(satz)) return null;
  if (stillMs !== null && !istZahl(stillMs, 0, 600_000)) return null;
  if (tts !== "lief" && tts !== "nein") return null;
  return {
    vorherId,
    abstandMs: Math.round(abstandMs),
    jev,
    satz: satz as FortsetzungSatz,
    stillMs: stillMs === null ? null : Math.round(stillMs),
    tts,
  };
}

/** Warum eine Angabe verworfen wurde — das Wort der Journalzeile `[fortsetzung] verworfen=`. */
type FortsetzungGrund = "aus" | "typ" | "konto" | "unbekannt" | "nicht-selbst" | "fenster" | "frequenz";

/** Die letzte Sprechtasten-Nachricht dieses Kontos, wie der Server sie kennt (nur im Arbeitsspeicher). */
export interface VorigerZug {
  /** Die `anfrageKennung` ihres Uploads. */
  kennung: string;
  /** Wann der Upload ankam (ms, Uhr des Servers). */
  ankunftMs: number;
  /** Endete die Aufnahme nach Stille (Auto-Senden)? */
  vonSelbst: boolean;
}

export interface FortsetzungPruefung {
  /** Schalter `FORTSETZUNG` (Standard an). */
  an: boolean;
  /** Das Konto der Anfrage; die Fortsetzung gilt nur für `erlaubtesKonto` (Konto 1, wie Jev). */
  uid: number;
  erlaubtesKonto: number;
  /** Die gelesene Angabe — `null`, wenn das Feld da war, aber nicht stimmte (`leseFortsetzungAngabe`). */
  angabe: FortsetzungAngabe | null;
  /** Die vorige Nachricht dieses Kontos, VOR der Anmeldung der jetzigen gelesen. */
  vorher: VorigerZug | null;
  /** Die Uhr des Servers (ms). */
  jetztMs: number;
  /** Hat das Konto in der letzten Minute noch eine Fortsetzung frei? */
  frequenzFrei: boolean;
}

type FortsetzungBefund = { ok: true; angabe: FortsetzungAngabe; vorher: VorigerZug } | { ok: false; grund: FortsetzungGrund };

/**
 * Gilt die Angabe? Rein. Die Reihenfolge ist die der Gründe: Schalter, Konto, Form, Kennung, Herkunft der vorigen Nachricht, Fenster, Frequenz.
 * Im Zweifel gilt sie NICHT — dann bleibt alles wie vorher (eine Nachricht, ohne Hinweis).
 */
export function pruefeFortsetzung(p: FortsetzungPruefung): FortsetzungBefund {
  if (!p.an) return { ok: false, grund: "aus" };
  if (p.uid !== p.erlaubtesKonto) return { ok: false, grund: "konto" };
  if (!p.angabe) return { ok: false, grund: "typ" };
  const vorher = p.vorher;
  if (!vorher || vorher.kennung === "" || vorher.kennung !== p.angabe.vorherId) return { ok: false, grund: "unbekannt" };
  if (!vorher.vonSelbst) return { ok: false, grund: "nicht-selbst" };
  const alter = p.jetztMs - vorher.ankunftMs;
  if (
    p.angabe.abstandMs > FORTSETZUNG_FENSTER_MS + FORTSETZUNG_TOLERANZ_MS ||
    alter < 0 ||
    alter > p.angabe.abstandMs + FORTSETZUNG_AUFNAHME_MAX_MS
  ) {
    return { ok: false, grund: "fenster" };
  }
  if (!p.frequenzFrei) return { ok: false, grund: "frequenz" };
  return { ok: true, angabe: p.angabe, vorher };
}

/*
 * ── Der Hinweis an das Modell ─────────────────────────────────────────────
 *
 * FESTER Text im Code (nie ein Wort aus dem Gesagten, nie aus dem Feld). Er steht dem Gesagten VORAN — im Text, den das Modell
 * liest (`gesprochenFuerModell` in server/voice-command.ts) — und nie in `anfrageZug.nachricht`, an der Zustimmungs- und
 * Fremdtext-Sperren hängen.
 */
/**
 * Was mit der Antwort der Vorrunde wirklich geschah — vom SERVER gewusst, nicht aus der Uhr geschlossen (Prüfung 12.10.2026):
 *   `abgebrochen`  ein Abbruch hat einen laufenden Zug getroffen (Rückgabewert von `interrupt()`),
 *   `laeuft`       sie läuft noch und ließ sich nicht abbrechen (Gemini-/API-Ersatzweg, Zeitüberschreitung beim Warten auf eine Handlung),
 *   `fertig`       sie war schon zu Ende, niemand musste etwas abbrechen („kam zu früh“).
 * Die App ruft /api/chat/abort bei jedem Start durch einen Menschen — der bloße Ruf sagt also nichts darüber, ob etwas getroffen wurde.
 */
export type FortsetzungVorrunde = "abgebrochen" | "laeuft" | "fertig";

/** Eine Handlung (Werkzeug mit Wirkung), die die Vorrunde schon begonnen oder getan hat — nur Name und Stand, nie ein Inhalt. */
export interface VorrundeHandlung {
  tool: string;
  /** `offen`: begonnen, das Ergebnis kam nicht mehr an (die Antwort wurde unterbrochen, die Handlung läuft am Server zu Ende). */
  status: "ok" | "fehler" | "offen";
}

/**
 * SICHERHEIT (Prüfung 12.10.): der Satz steht in JEDER Fassung. „Betrachte beide als EINE“ darf das Modell nicht dazu einladen, ein „ja“ der zweiten
 * Hälfte als Antwort auf eine Rückfrage der ersten zu lesen — Martin hat sie nie gehört (der Druck stoppt die Wiedergabe). Der Server sichert es zusätzlich
 * (der Zug beginnt mit der Vorrunde, tools/versand-bestaetigung.ts).
 */
export const FORTSETZUNG_FREIGABE = " Bestätigungen und Freigaben der ersten Äußerung gelten nicht; frage neu.";

/** Die Vorrunde wurde abgebrochen (ein Abbruch traf den laufenden Zug): die Fassung der Aufgabe. */
export const FORTSETZUNG_HINWEIS =
  "[Fortsetzung: Martin hat seinen vorigen Satz noch nicht beendet. Deine begonnene Antwort wurde abgebrochen. " +
  "Betrachte beide Äußerungen zusammen als EINE und antworte jetzt auf das Ganze." +
  FORTSETZUNG_FREIGABE +
  "]";
/**
 * Die Vorrunde lief schon zu Ende und musste nicht abgebrochen werden: „abgebrochen“ wäre falsch, und ein neuer Gedanke binnen 12 s ist nicht
 * sicher eine Fortsetzung. Darum die weiche Fassung, die beides offen lässt.
 */
export const FORTSETZUNG_HINWEIS_FRUEH =
  "[Fortsetzung: Martin hat seinen vorigen Satz womöglich noch nicht beendet. Deine Antwort darauf kam zu früh. " +
  "Gehört die neue Äußerung zum vorigen Satz, betrachte beide zusammen als EINE und antworte auf das Ganze; sonst beantworte sie als neue Frage." +
  FORTSETZUNG_FREIGABE +
  "]";
/** Die Vorrunde läuft noch und ließ sich nicht abbrechen (zum Beispiel auf einem Ersatzweg): sie kann noch Handlungen ausführen. */
export const FORTSETZUNG_HINWEIS_LAEUFT =
  "[Fortsetzung: Martin hat seinen vorigen Satz womöglich noch nicht beendet. Deine Antwort darauf läuft noch weiter und ließ sich nicht abbrechen. " +
  "Gehört die neue Äußerung zum vorigen Satz, betrachte beide zusammen als EINE und antworte auf das Ganze; sonst beantworte sie als neue Frage. " +
  "Wiederhole nichts, was die laufende Antwort schon tut oder getan hat." +
  FORTSETZUNG_FREIGABE +
  "]";

/** Name einer Handlung im Hinweis: nur ein kurzer Werkzeugname, sonst „unbekannt“ — nichts Fremdes gelangt in den festen Text. */
const HANDLUNG_NAME = /^[A-Za-z0-9_.-]{1,60}$/;
/** Höchstens so viele Handlungen nennt der Hinweis. */
const HANDLUNGEN_MAX = 8;
const STAND_WORT: Record<VorrundeHandlung["status"], string> = { ok: "ok", fehler: "Fehler", offen: "Ergebnis offen" };

/** Der Satz über die Handlungen der Vorrunde — leer, wenn es keine gab. Nur Name und Stand, nie ein Inhalt (kein Empfänger, kein Betreff, kein Betrag). */
export function handlungenSatz(handlungen: readonly VorrundeHandlung[]): string {
  if (handlungen.length === 0) return "";
  const teile = handlungen
    .slice(0, HANDLUNGEN_MAX)
    .map((h) => `${HANDLUNG_NAME.test(h.tool) ? h.tool : "unbekannt"} (${STAND_WORT[h.status]})`);
  const mehr = handlungen.length > HANDLUNGEN_MAX ? ` und ${handlungen.length - HANDLUNGEN_MAX} weitere` : "";
  return (
    ` Bereits ausgeführt in der Vorrunde: ${teile.join(", ")}${mehr}.` +
    " Führe das nicht ein zweites Mal aus; prüfe vor einer Wiederholung."
  );
}

/** Der Hinweis für diese Lage: die Fassung nach dem, was mit der Vorrunde geschah, und — falls es welche gab — ihre Handlungen. */
export function fortsetzungHinweis(vorrunde: FortsetzungVorrunde, handlungen: readonly VorrundeHandlung[] = []): string {
  const basis = vorrunde === "abgebrochen" ? FORTSETZUNG_HINWEIS : vorrunde === "laeuft" ? FORTSETZUNG_HINWEIS_LAEUFT : FORTSETZUNG_HINWEIS_FRUEH;
  const satz = handlungenSatz(handlungen);
  return satz === "" ? basis : `${basis.slice(0, -1)}${satz}]`;
}

/** Das Gesagte mit dem Hinweis davor — genau einmal (steht er schon da, bleibt der Text, wie er ist). */
export function mitFortsetzungsHinweis(text: string, hinweis: string): string {
  return text.startsWith(hinweis) ? text : `${hinweis}\n${text}`;
}

/**
 * Ist diese Verlaufszeile die HALBE Antwort einer gestoppten Vorrunde (`metadata.abgebrochen`)? (12.10.2026, Prüfung)
 * Das Gedächtnis liest sie nicht: eine abgebrochene Antwort ist kein Beleg für etwas (weder für Fakten über Martin — die kommen ohnehin nur aus
 * SEINEN Zeilen — noch für eine Zusammenfassung dessen, was gesagt wurde). Sie bleibt im Verlauf und in der Datenbank, nichts wird gelöscht.
 */
export function istAbgebrocheneAntwort(role: string, metadata: unknown): boolean {
  return role === "assistant" && typeof metadata === "object" && metadata !== null && (metadata as { abgebrochen?: unknown }).abgebrochen === true;
}

/*
 * ── Die Journalzeilen: nur Zahlen und feste Wörter ─────────────────────────
 */

/** Das Wort der Vorrunde in der Zeile: `abgebrochen`, `laeuft-weiter` oder `zu-frueh`. */
const VORRUNDE_WORT: Record<FortsetzungVorrunde, string> = { abgebrochen: "abgebrochen", laeuft: "laeuft-weiter", fertig: "zu-frueh" };

/**
 * `[fortsetzung] erkannt=ja …` — die Messung der Abschneiderate. `antwortAbgebrochen=ja` heißt jetzt: ein Abbruch traf wirklich einen laufenden Zug
 * (nicht: die App hat /api/chat/abort gerufen). `handlungen=` zählt die Handlungen der Vorrunde, die der Hinweis nennt.
 */
export function fortsetzungErkanntZeile(uid: number, a: FortsetzungAngabe, vorrunde: FortsetzungVorrunde, handlungen = 0): string {
  return (
    `[fortsetzung] erkannt=ja abstandMs=${a.abstandMs} vorherJev=${a.jev === null ? "-" : a.jev.toFixed(2)} vorherSatz=${a.satz}` +
    ` vorherStillMs=${a.stillMs ?? "-"} antwortAbgebrochen=${vorrunde === "abgebrochen" ? "ja" : "nein"} tts=${a.tts}` +
    ` hinweis=${VORRUNDE_WORT[vorrunde]} handlungen=${handlungen} uid=${uid}`
  );
}

/** `[fortsetzung] verworfen=<Grund>` — die Angabe war ungültig, die Nachricht lief wie jede andere. */
export function fortsetzungVerworfenZeile(uid: number, grund: FortsetzungGrund): string {
  return `[fortsetzung] verworfen=${grund} uid=${uid}`;
}
